import axios from 'axios';
import { LinkTypes, ReturnCodes } from '../utils/constants';
import fileSystemAccessService from './FileSystemAccessService';
import {getAuthHeaders} from '../utils/authUtils';

class VideoCRUDService {
    createPostRequest(
        link_Type,
        isCompressed,
        selectOpenAI,
        isLogging,
        skipTranslationIsChecked,
        onlyTransliteration,
        bitRate,
        crfValue,
        folderPath = null,
    ) {
        const useMongoDb = fileSystemAccessService.shouldUseMongoDBForVideos();

        // Base URL depends on video experience
        let url = '';
        if(useMongoDb) {
            // For MongoDB, use v3 API
            // This is the new API for MongoDB
            if (folderPath) {
                url = `${process.env.REACT_APP_API_URL}/api/v3/videos/createInFolder`;
            }else{
                url = `${process.env.REACT_APP_API_URL}/api/v3/videos`;
            }
        } else {
            // For non-MongoDB, use v2 API
            url = `${process.env.REACT_APP_API_URL}/api/v2/videos/v2`;
        }

        // Add query parameters
        let options = url;

        if (isCompressed) options += '?compressed=true';
        else options += '?compressed=false';

        if(useMongoDb && folderPath) {
            options += '&folder=' + folderPath;
        }

        if (selectOpenAI) options += '&modelSelection=openai';
        else options += '&modelSelection=custom';

        if (isLogging) options += '&detailedLogging=true';
        else options += '&detailedLogging=false';

        if (skipTranslationIsChecked) options += '&skipTranslation=true';
        else options += '&skipTranslation=false';

        if (onlyTransliteration) options += '&onlyTransliteration=true';
        else options += '&onlyTransliteration=false';

        if (bitRate) options += '&bitRate=' + bitRate;
        if (crfValue) options += '&crf=' + crfValue;

        if (link_Type === LinkTypes.YOUTUBE) {
            options += '&link=youtube';
        } else if (link_Type === LinkTypes.LOCAL_FILE) {
            options += '&link=local';
        } else {
            options += '&link=gdrive';
        }

        const selectedVideoLanguages = JSON.parse(localStorage.getItem('createVideoLanguages'));
        console.log('selectedVideoLanguages:', selectedVideoLanguages);
        if (selectedVideoLanguages !== null) {
            // selectedVideoLanguages is in the format of "[{value: 'eng', label: 'English', isFixed: true}]"
            // just read the value of each item and join them with a comma
            options += '&createLanguages=' + selectedVideoLanguages.join(',');
        }else{
            options += '&createLanguages=eng';
        }

        return options;
    }

    async isVideoURLValid(url, youTubeURL, linkType) {
        if (linkType === LinkTypes.YOUTUBE) {
            const youTubeVideoName = youTubeURL.split('v=')[1];
            const apiKey = process.env.REACT_APP_YOUTUBE_API_KEY;
            url = `https://www.googleapis.com/youtube/v3/videos?id=${youTubeVideoName}&key=${apiKey}&part=snippet,contentDetails,statistics,status`;
        }

        const response = await axios.get(url);
        if (response.status === 200) {
            return true;
        } else {
            return false;
        }
    }

    async addVideoInFolderWithPromise(
        link_Type,
        url,
        userData,
        videoName,
        folderPath,
        isCompressed,
        selectOpenAI,
        isLogging,
        skipTranslationIsChecked,
        onlyTransliteration,
        bitRate,
        crfValue,
    ) {
        return new Promise(async (resolve, reject) => {
            try {
                const useMongoDb = fileSystemAccessService.shouldUseMongoDBForVideos();
                let apiEndpoint;

                if (folderPath) {
                    // For videos in folders, use appropriate API based on experience
                    apiEndpoint = this.createPostRequest(
                        link_Type,
                        isCompressed,
                        selectOpenAI,
                        isLogging,
                        skipTranslationIsChecked,
                        onlyTransliteration,
                        bitRate,
                        crfValue,
                        folderPath,
                    );
                } else {
                    // For root folder or simple video add
                    apiEndpoint = this.createPostRequest(
                        link_Type,
                        isCompressed,
                        selectOpenAI,
                        isLogging,
                        skipTranslationIsChecked,
                        onlyTransliteration,
                        bitRate,
                        crfValue,
                    );
                }

                await axios
                    .post(
                        apiEndpoint,
                        {
                            videoUrl: url,
                            videoName: videoName,
                        },
                        {
                            headers: {
                                'Access-Control-Allow-Origin': '*',
                                'Access-Control-Allow-Credentials': true,
                                email: userData.email,
                                auth: userData.access,
                            },
                        },
                    )
                    .then(function (response) {
                        // If using MongoDB API with folderPath, the video is already added to the folder
                        if (useMongoDb && folderPath && folderPath !== '/') {
                            resolve({
                                status: ReturnCodes.FILE_UPLOAD_SUCCESS,
                                id: response.data.id,
                                videoName: response.data.videoName,
                                ...response.data
                            });
                        } else {
                            // Old flow - call addVideo2Folder if needed
                            if (folderPath && folderPath !== '/') {
                                fileSystemAccessService
                                    .addVideo2Folder(folderPath, response.data.videoUrl, response.data.id, userData)
                                    .then(function (folderResponse) {
                                        resolve({
                                            status: ReturnCodes.FILE_UPLOAD_SUCCESS,
                                            id: response.data.id,
                                            videoName: response.data.videoName,
                                            ...response.data
                                        });
                                    })
                                    .catch(function (error) {
                                        console.error('Error adding video to folder:', error);
                                        reject({status: ReturnCodes.FILE_UPLOAD_FAILED});
                                    });
                            } else {
                                resolve({
                                    status: ReturnCodes.FILE_UPLOAD_SUCCESS,
                                    id: response.data.id,
                                    videoName: response.data.videoName,
                                    ...response.data
                                });
                            }
                        }
                    })
                    .catch(function (error) {
                        console.error('Error uploading video:', error);
                        reject({status: ReturnCodes.FILE_UPLOAD_FAILED});
                    });
            } catch (error) {
                console.error('Exception in addVideoInFolderWithPromise:', error);
                reject({status: error});
            }
        });
    }

    /**
     * Delete a video by ID, supporting both SQL and MongoDB backends
     * 
     * @param {string} videoid - The ID of the video to delete
     * @param {string} apisurls - The folder path where the video is located
     * @param {string} email - User's email
     * @param {string} access - User's access token
     * @returns {Promise} - Promise resolving to the API response
     */
    async delete(videoid, apisurls, email, access) {
        return new Promise(async (resolve, reject) => {
            try {
                const useMongoDb = fileSystemAccessService.shouldUseMongoDBForVideos();
                const authUserEmail = JSON.parse(localStorage.getItem('authUser')).email;
                const authToken = JSON.parse(localStorage.getItem('authToken'));          
                // Determine the correct API endpoint based on database type
                const apiBaseUrl = process.env.REACT_APP_API_URL;
                const apiEndpoint = useMongoDb 
                    ? `${apiBaseUrl}/api/v3/videos/${videoid}?folder=${encodeURIComponent(apisurls)}`
                    : `${apiBaseUrl}/api/v2/videos/${videoid}?folder=${encodeURIComponent(apisurls)}`;

                const response = await axios.delete(apiEndpoint, {
                    headers: {
                        email: authUserEmail,
                        auth: authToken, // Use 'auth' consistently
                        'Access-Control-Allow-Origin': '*',
                        'Access-Control-Allow-Credentials': true,
                    },
                });
                
                resolve(response.data);
            } catch (error) {
                console.error('Error deleting video:', error);
                reject(error);
            }
        });
    }

    async getVideoDataOfIds(videoIdList, email, access) {
        const useMongoDb = fileSystemAccessService.shouldUseMongoDBForVideos();
        const apiUrl = useMongoDb 
            ? `${process.env.REACT_APP_API_URL}/api/v3/videos/all/${videoIdList}`
            : `${process.env.REACT_APP_API_URL}/api/v2/videos/all/${videoIdList}`;
            
        try {
            const response = await axios.get(apiUrl, {
                headers: {
                    email: email,
                    auth: access,
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Credentials': true,
                }
            });
            return response;
        } catch (error) {
            console.error('Error fetching videos by IDs:', error);
            throw error;
        }
    }

    /**
     * Rename a video by ID, supporting both SQL and MongoDB backends
     * 
     * @param {string|number} videoId - The ID of the video to rename
     * @param {string} email - User's email
     * @param {string} access - User's access token
     * @param {string} newName - New name for the video
     * @returns {Promise} - Promise resolving to the API response
     */
    async rename(videoId, currentPath = null, email, access, newName) {
        return new Promise(async (resolve, reject) => {
            try {
                const useMongoDb = fileSystemAccessService.shouldUseMongoDBForVideos();
                
                // Determine the correct API endpoint based on database type
                const apiBaseUrl = process.env.REACT_APP_API_URL;
                const apiEndpoint = useMongoDb
                    ? `${apiBaseUrl}/api/v3/videos/rename?id=${encodeURIComponent(videoId)}&name=${encodeURIComponent(newName)}&currentPath=${encodeURIComponent(currentPath)}`
                    : `${apiBaseUrl}/api/v2/videos/rename?id=${encodeURIComponent(videoId)}&name=${encodeURIComponent(newName)}`;
                
                const response = await axios.put(
                    apiEndpoint,
                    {}, // Empty body
                    {
                        headers: {
                            email: email,
                            auth: access, // Use 'auth' consistently
                            'Access-Control-Allow-Origin': '*',
                            'Access-Control-Allow-Credentials': true,
                        },
                    }
                );
                
                resolve(response);
            } catch (error) {
                console.error('Error renaming video:', error);
                reject(error);
            }
        });
    }

    /**
     * Regenerate transcriptions for a video, supporting both SQL and MongoDB backends
     * 
     * @param {string|number} videoId - The ID of the video to regenerate
     * @param {string} email - User's email
     * @param {string} access - User's access token
     * @returns {Promise} - Promise resolving to the API response
     */
    async regenerate(videoId, email, access) {
        try {
            const useMongoDb = fileSystemAccessService.shouldUseMongoDBForVideos();
            
            // Determine the correct API endpoint based on database type
            const apiBaseUrl = process.env.REACT_APP_API_URL;
            const apiEndpoint = useMongoDb
                ? `${apiBaseUrl}/api/v3/videos/regenerate?id=${encodeURIComponent(videoId)}`
                : `${apiBaseUrl}/api/v2/videos/regenerate?id=${encodeURIComponent(videoId)}`;
                
            const response = await axios.put(
                apiEndpoint,
                {}, // Empty body
                {
                    headers: {
                        email: email,
                        auth: access, // Use 'auth' consistently
                        'Access-Control-Allow-Origin': '*',
                        'Access-Control-Allow-Credentials': true,
                    },
                },
            );
            return response;
        } catch (error) {
            console.error('Error regenerating video transcription:', error);
            throw error;
        }
    }

    async updateProgressStage(videoId, email, access, processStage, languageName) {
        try {
            const response = await axios.put(
                process.env.REACT_APP_API_URL +
                    `/api/v2/videos/processStage?id=${videoId}&newProcessStage=${processStage}&languageName=${languageName}`,
                {},
                {
                    headers: {
                        email: email,
                        access: access,
                        'Access-Control-Allow-Origin': '*',
                        'Access-Control-Allow-Credentials': true,
                    },
                },
            );
            return response;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Update the process stage for a specific language in a video
     * 
     * @param {string|number} videoId - The ID of the video
     * @param {string} languageName - The language name to update
     * @param {string} newProcessStage - The new process stage value
     * @param {string} email - User's email
     * @param {string} access - User's access token
     * @param {boolean} useMongoDb - Whether to use MongoDB API
     * @returns {Promise} - Promise resolving to the API response
     */
    async updateProcessStage(videoId, languageName, newProcessStage, email, access, useMongoDb = false) {
        try {
            // Determine the correct API endpoint based on database type
            const apiBaseUrl = process.env.REACT_APP_API_URL;
            const apiEndpoint = useMongoDb
                ? `${apiBaseUrl}/api/v3/videos/processStage`
                : `${apiBaseUrl}/api/v2/videos/processStage`;
                
            // Build query parameters
            const params = new URLSearchParams();
            params.append('id', videoId);
            params.append('languageName', languageName);
            params.append('newProcessStage', newProcessStage);
            
            const response = await axios.put(
                `${apiEndpoint}?${params.toString()}`,
                {}, // Empty body
                {
                    headers: {
                        email: email,
                        auth: access,
                        'Access-Control-Allow-Origin': '*',
                        'Access-Control-Allow-Credentials': true,
                    },
                },
            );
            return response;
        } catch (error) {
            console.error('Error updating process stage:', error);
            throw error;
        }
    }

    async text2text(videoIdOption, targetLanguagesOption, sourceLanguageOption, skipTranslation) {
        return new Promise(async (resolve, reject) => {
            try {
                await axios
                    .post(
                        process.env.REACT_APP_API_URL +
                            `/api/v3/videos/text2text/` + // TODO: Test this endpoint
                            videoIdOption +
                            targetLanguagesOption +
                            sourceLanguageOption +
                            skipTranslation,
                        {
                            headers: { 'Access-Control-Allow-Origin': '*', 'Access-Control-Allow-Credentials': true },
                        },
                    )
                    .then((response) => {
                        resolve(response);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Initiate text-to-text translation for a video, supporting both SQL and MongoDB backends
     * 
     * @param {string|number} videoId - The ID of the video
     * @param {string} sourceLanguage - The source language to translate from
     * @param {string} targetLanguages - Comma-separated list of languages to translate to
     * @param {boolean} skipTranslation - Whether to skip translation and create empty files
     * @param {string} email - User's email
     * @param {string} access - User's access token
     * @returns {Promise} - Promise resolving to the API response
     */
    async text2Text(videoId, sourceLanguage, targetLanguages, skipTranslation, email, access) {
        try {
            const useMongoDb = fileSystemAccessService.shouldUseMongoDBForVideos();
            
            // Determine the correct API endpoint based on database type
            const apiBaseUrl = process.env.REACT_APP_API_URL;
            const apiEndpoint = useMongoDb
                ? `${apiBaseUrl}/api/v3/videos/text2text`
                : `${apiBaseUrl}/api/v2/videos/text2text/`;
                
            // Build query parameters
            const params = new URLSearchParams();
            params.append('videoId', videoId);
            params.append('sourceLanguage', sourceLanguage);
            params.append('targetLanguages', targetLanguages);
            params.append('skipTranslation', skipTranslation);
            
            const response = await axios.post(
                `${apiEndpoint}?${params.toString()}`,
                {}, // Empty body
                {
                    headers: {
                        email: email,
                        auth: access,
                        'Access-Control-Allow-Origin': '*',
                        'Access-Control-Allow-Credentials': true,
                    },
                },
            );
            return response;
        } catch (error) {
            console.error('Error in text-to-text translation:', error);
            throw error;
        }
    }

    async getVideos() {
        const useMongoDb = fileSystemAccessService.shouldUseMongoDBForVideos();
        const apiUrl = useMongoDb 
            ? `${process.env.REACT_APP_API_URL}/api/v3/videos`
            : `${process.env.REACT_APP_API_URL}/api/v2/videos`;
            
        try {
            const response = await axios.get(apiUrl, {
                headers: { 'Access-Control-Allow-Origin': '*', 'Access-Control-Allow-Credentials': true },
            });
            return response;
        } catch (error) {
            throw error;
        }
    }

    async getVideoById(videoId, email, access) {
        const useMongoDb = fileSystemAccessService.shouldUseMongoDBForVideos();
        const apiUrl = useMongoDb 
            ? `${process.env.REACT_APP_API_URL}/api/v3/videos/${videoId}`
            : `${process.env.REACT_APP_API_URL}/api/v2/videos/${videoId}`;
            
        try {
            const response = await axios.get(apiUrl, {
                headers: {
                    email: email,
                    access: access,
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Credentials': true,
                }
            });
            return response;
        } catch (error) {
            console.error('Error fetching video details:', error);
            throw error;
        }
    }

    async getVideoDataOfId(videoId, email, access) {
        if (fileSystemAccessService.shouldUseMongoDBForVideos()) {
            return this.getVideoById(videoId, email, access);
        }

        return new Promise(async (resolve, reject) => {
            try {
                await axios
                    .get(process.env.REACT_APP_API_URL + `/api/v2/videos/${videoId}`, {
                        headers: {
                            'Access-Control-Allow-Origin': '*',
                            'Access-Control-Allow-Credentials': true,
                            email: email,
                            access: access,
                        },
                    })
                    .then((response) => {
                        resolve(response);
                    })
                    .catch((error) => {
                        reject(error);
                    });
            } catch (error) {
                reject(error);
            }
        });
    }

    /**
     * Save metadata for a video by ID, supporting both SQL and MongoDB backends
     * 
     * @param {string|number} videoId - The ID of the video
     * @param {boolean} bookmarked - Whether the video is bookmarked
     * @param {string} subtitleComment - Comment for the subtitle
     * @param {string} languageName - The language name
     * @param {string} email - User's email
     * @param {string} access - User's access token
     * @returns {Promise} - Promise resolving to the API response
     */
    async saveMetaDataforVideoId(videoId, bookmarked, subtitleComment, languageName, email, access, saveType="manual", importSource = null) {
        try {
            const useMongoDb = fileSystemAccessService.shouldUseMongoDBForVideos();
            const authUserEmail = email || JSON.parse(localStorage.getItem('authUser'))?.email;
            const authToken = access || JSON.parse(localStorage.getItem('authToken'));
            // Determine the correct API endpoint based on database type
            const apiBaseUrl = process.env.REACT_APP_API_URL;
            const apiEndpoint = useMongoDb
                ? `${apiBaseUrl}/api/v3/videos/${videoId}`
                : `${apiBaseUrl}/api/v2/videos/${videoId}`;
                
            const response = await axios.put(
                apiEndpoint,
                {
                    languageName: languageName,
                    metadataCheckFlag: bookmarked,
                    metadataComments: subtitleComment,
                    saveType : saveType,
                    importSource: importSource,
                },
                {
                    headers: {
                        email: authUserEmail,
                        auth: authToken,
                        'Access-Control-Allow-Origin': '*',
                        'Access-Control-Allow-Credentials': true,
                    },
                },
            );
            return response;
        } catch (error) {
            console.error('Error saving metadata for video:', error);
            throw error;
        }
    }

    async getVideosLogs(videoId) {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v2/status/${videoId}`, {
                headers: { 'Access-Control-Allow-Origin': '*', 'Access-Control-Allow-Credentials': true },
            });
            return response;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Delete multiple videos by ID, supporting both SQL and MongoDB backends
     * 
     * @param {string|array} videos - Single ID or array of video IDs to delete
     * @param {string} folder - The folder path where the videos are located
     * @param {string} email - User's email
     * @param {string} access - User's access token
     * @returns {Promise} - Promise resolving to the API response
     */
    async deleteVideos(videos, folder, email, access) {
        try {
            const useMongoDb = fileSystemAccessService.shouldUseMongoDBForVideos();
            
            // Convert array to comma-separated string if needed
            const videoIds = Array.isArray(videos) ? videos.join(',') : videos;
            
            // Determine the correct API endpoint based on database type
            const apiBaseUrl = process.env.REACT_APP_API_URL;
            const apiEndpoint = useMongoDb 
                ? `${apiBaseUrl}/api/v3/videos/all/${videoIds}?folder=${encodeURIComponent(folder)}`
                : `${apiBaseUrl}/api/v2/videos/all/${videoIds}?folder=${encodeURIComponent(folder)}`;

            const response = await axios.delete(apiEndpoint, {
                headers: {
                    email: email,
                    auth: access, // Use 'auth' consistently
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Credentials': true,
                },
            });
            
            return response.data;
        } catch (error) {
            console.error('Error deleting multiple videos:', error);
            throw error;
        }
    }

    /**
 * Move a video file to a different folder
 * 
 * @param {string} videoId - The ID of the video to move
 * @param {string} sourcePath - Current path of the video
 * @param {string} destinationPath - Path to move the video to
 * @param {string} email - User's email
 * @param {string} access - User's access token
 * @returns {Promise} - Promise resolving to the API response
 */
async moveFile(videoId, sourcePath, destinationPath, email, access) {
    try {
        const useMongoDb = fileSystemAccessService.shouldUseMongoDBForVideos();
        const apiBaseUrl = process.env.REACT_APP_API_URL;
        
        const apiEndpoint = useMongoDb
            ? `${apiBaseUrl}/api/v3/videos/${videoId}/move`
            : `${apiBaseUrl}/api/v2/videos/${videoId}/move`;
        
        const response = await axios.post(
            apiEndpoint,
            {
                sourcePath,
                destinationPath
            },
            {
                headers: {
                    email: email,
                    auth: access,
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Credentials': true,
                },
            }
        );
        
        return response.data;
    } catch (error) {
        console.error('Error moving video file:', error);
        throw error;
    }
}

    /**
     * Saves subtitle content and related metadata for a video
     * 
     * @param {Array} subtitle - The subtitle data to save
     * @param {string} selectedLng - The language being edited
     * @param {boolean} bookmarked - Whether the video is bookmarked
     * @param {string} subtitleComment - Comments on the subtitle
     * @param {Function} addLog - Function to log actions
     * @param {boolean} showFeedback - Whether to show user feedback/alerts
     * @returns {Promise<Object>} Result object with success status and response data
     */
    async saveSubtitleContent(subtitle, selectedLng, bookmarked, subtitleComment, addLog = () => {}, showFeedback = true, importSource = null) {
        try {
            // Parse video properties
            const videoProps = JSON.parse(localStorage.getItem('videoProps'));
            
            // Don't attempt to save if no subtitles exist
            if (!subtitle || subtitle.length === 0) {
                return { success: false, message: "No subtitles to save" };
            }
            
            // Find the language index in videoProps
            let languageIndex = -1;
            for (let i = 0; i < videoProps.videoLanguages.length; i++) {
                if (videoProps.videoLanguages[i].languageName === selectedLng) {
                    languageIndex = i;
                    break;
                }
            }
            
            if (languageIndex === -1) {
                const message = `Language '${selectedLng}' not found in video properties`;
                console.error(message);
                return { success: false, message };
            }
            
            const vttFileURL = videoProps.videoLanguages[languageIndex].vttFileUrl;
            
            // Check user permissions using our updated method
            if (!this.areSubtitlesEditable(selectedLng)) {
                const authUser = JSON.parse(localStorage.getItem('authUser'));
                const message = `User ${authUser?.email || 'Unknown'} does not have permission to edit ${selectedLng} subtitles`;
                
                if (showFeedback && window.Swal) {
                    window.Swal.fire({
                        title: 'Permission Denied',
                        text: message,
                        icon: 'error',
                        confirmButtonText: 'Close',
                    });
                }
                
                addLog(`Permission denied: ${message} for Video = ${videoProps.id}, VideoName = ${videoProps.videoName}`);
                return { success: false, message };
            }
            
            try {
                // Create parsed subtitle object
                const parsedSubtitle = {
                    cues: [],
                    valid: true,
                };
                
                // Convert subtitle format for VTT and ensure time values are properly formatted
                subtitle.forEach((subtitleBlock) => {
                    // Convert time strings to seconds for the compiler
                    const startSeconds = this.convertTimeToSeconds(subtitleBlock.start);
                    const endSeconds = this.convertTimeToSeconds(subtitleBlock.end);
                    
                    // Create the cue with properly formatted times
                    const cue = {
                        identifier: '',
                        start: startSeconds,  // Use numeric seconds value
                        end: endSeconds,      // Use numeric seconds value
                        text: subtitleBlock.text2 || subtitleBlock.text || '',
                        settings: '',         // VTT settings string
                    };
                    
                    // Add additional properties if they exist
                    if (subtitleBlock.Estart) cue.Estart = this.convertTimeToSeconds(subtitleBlock.Estart);
                    if (subtitleBlock.Eend) cue.Eend = this.convertTimeToSeconds(subtitleBlock.Eend);
                    if (subtitleBlock.Etext) cue.Etext = subtitleBlock.Etext;
                    if (subtitleBlock.note) cue.note = subtitleBlock.note;
                    if (subtitleBlock.bookmark) cue.bookmark = subtitleBlock.bookmark;
                    
                    parsedSubtitle.cues.push(cue);
                });
                
                // Filter out empty cues and cues with invalid times
                parsedSubtitle.cues = parsedSubtitle.cues.filter((cue) => {
                    // Ensure both times are valid numbers
                    return (
                        !isNaN(cue.start) && 
                        !isNaN(cue.end) && 
                        (cue.start > 0 || cue.end > 0 || cue.text !== '')
                    );
                });
                
                if (parsedSubtitle.cues.length === 0) {
                    const message = "No valid subtitles to save after filtering";
                    console.error(message);
                    return { success: false, message };
                }
                
                // Log for debugging
                console.log('Prepared cues for compilation:', parsedSubtitle.cues[0]);
                
                // Compile the subtitle content to VTT format
                const { compile } = await import('../libs/webvttnotes/compiler');
                const modifiedSubtitleContent = compile(parsedSubtitle, { strict: false });
                
                // Import and call the blob storage function
                addLog(
                    `Attempting to save to Blob Storage: Video = ${videoProps.id}, VideoName = ${videoProps.videoName}, Language = ${selectedLng}`
                );
                
                // Import the blob storage function and upload
                const { uploadToBlobStorage } = await import('../folders/subtitles/BlobVttFile');
                await uploadToBlobStorage(vttFileURL, modifiedSubtitleContent);
                
                addLog(
                    `Successfully saved to Blob Storage: Video = ${videoProps.id}, VideoName = ${videoProps.videoName}, Language = ${selectedLng}`
                );
                
                // Save metadata
                const authUser = JSON.parse(localStorage.getItem('authUser'));
                const authToken = localStorage.getItem('authToken');
                
                const response = await this.saveMetaDataforVideoId(
                    videoProps.id, 
                    bookmarked, 
                    subtitleComment, 
                    selectedLng,
                    authUser?.email,
                    authToken,
                    showFeedback ? "manual" : (importSource == null) ? "auto" : "import",
                    importSource
                );

                
                addLog(
                    `${showFeedback ? '' : 'Auto '}Saved metadata for Video = ${videoProps.id}, VideoName = ${videoProps.videoName}, Language = ${selectedLng}`
                );
                
                // Update localStorage with the updated video properties
                if (response && response.data) {
                    localStorage.setItem('videoProps', JSON.stringify(response.data));
                }
                
                // Show success feedback if requested
                if (showFeedback && window.Swal) {
                    window.Swal.fire({
                        title: 'Success!',
                        text: 'Your changes have been saved!',
                        icon: 'success',
                        confirmButtonText: 'Close',
                    });
                }
                
                return { success: true, data: response?.data };
                
            } catch (error) {
                console.error("Error saving subtitles:", error);
                
                addLog(
                    `Error ${showFeedback ? '' : 'auto '}saving subtitles: Video = ${videoProps.id}, VideoName = ${videoProps.videoName}, Language = ${selectedLng}, Error = ${error.message}`
                );
                
                if (showFeedback && window.Swal) {
                    window.Swal.fire({
                        title: 'Error!',
                        text: `Failed to save subtitles: ${error.message}`,
                        icon: 'error',
                        confirmButtonText: 'Close',
                    });
                }
                
                return { success: false, error };
            }
        } catch (error) {
            console.error("Error in saveSubtitleContent:", error);
            return { success: false, error };
        }
    }

    /**
     * Converts time string (HH:MM:SS.mmm) to seconds (as a number)
     * 
     * @param {string} timeString - Time in format "HH:MM:SS.mmm" or "MM:SS.mmm"
     * @returns {number} - Time in seconds
     */
    convertTimeToSeconds(timeString) {
        if (!timeString) return 0;
        
        // Handle various time formats
        const parts = timeString.split(':');
        let hours = 0, minutes = 0, seconds = 0;
        
        if (parts.length === 3) {
            // HH:MM:SS format
            hours = parseInt(parts[0], 10) || 0;
            minutes = parseInt(parts[1], 10) || 0;
            seconds = parseFloat(parts[2]) || 0;
        } else if (parts.length === 2) {
            // MM:SS format
            minutes = parseInt(parts[0], 10) || 0;
            seconds = parseFloat(parts[1]) || 0;
        } else if (parts.length === 1) {
            // SS format
            seconds = parseFloat(parts[0]) || 0;
        }
        
        // Calculate total seconds
        return hours * 3600 + minutes * 60 + seconds;
    }

    /**
     * Helper method to check if subtitles are editable for a language based on user permissions
     * 
     * @param {string} language - The language to check
     * @returns {boolean} - Whether the language is editable for the current user
     */
    areSubtitlesEditable(language) {
        try {
            // Get the current authenticated user
            const authUser = JSON.parse(localStorage.getItem('authUser'));
            if (!authUser) return false;

            // Check if user has language-specific permissions in their profile
            if (authUser.languageAccess && typeof authUser.languageAccess === 'object') {
                // For admin users, check if they have explicit access to this language
                // This allows for more fine-grained control even for admins
                if (authUser.userType === 'admin') {
                    // If language is not specified in languageAccess, admins can edit it
                    if (!authUser.languageAccess[language]) return true;
                    
                    // Otherwise, check their specific access level
                    return (authUser.languageAccess[language] === 'WRITE' || authUser.languageAccess[language] === 'ADMIN');
                }
                
                // For non-admin users, check their specific language access
                const accessLevel = authUser.languageAccess[language];
                return accessLevel === 'WRITE'; 
            }
            
            // Fallback for users without languageAccess property
            // Admin users can edit all languages by default
            if (authUser.userType === 'admin') return true;
            
            // For backward compatibility - use the old hardcoded list
            const editableLanguages = ['English', 'Hindi', 'Gujarati'];
            return editableLanguages.includes(language);
        } catch (error) {
            console.error('Error checking subtitle edit permissions:', error);
            return false; // Default to no permission on error
        }
    }

    // Clear version history for a video
    clearVersionHistory(videoId) {
        const apiBaseUrl = process.env.REACT_APP_API_URL;
        
        const apiEndpoint = `${apiBaseUrl}/api/v3/videos/${videoId}/languages`

        return axios.delete(apiEndpoint);
    }

    /**
     * Gets the current processing status of a video
     * 
     * @param {string} videoId - The ID of the video
     * @param {string} email - User email
     * @param {string} auth - Authentication token
     * @returns {Promise} - Promise resolving to the processing status
     */
    async getVideoProcessingStatus(videoId, email, auth) {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/v3/videos/${videoId}/processing-status`,
                {
                    headers: {
                        email: email,
                        auth: auth
                    }
                }
            );
            return response;
        } catch (error) {
            console.error('Error getting video processing status:', error);
            throw error;
        }
    }

    async terminateTranslation(videoId, languageName) {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/v3/video/${videoId}/language/${languageName}/terminate`;
            const response = await axios.post(url, {}, { headers: getAuthHeaders(true) });
            return response.data;
        } catch (error) {
            console.error('Failed to terminate translation:', error);
            throw error;
        }
    }

}

const videoCRUDServiceInstance = new VideoCRUDService();
export default videoCRUDServiceInstance;
